import React, { useRef } from 'react';
import { Stack } from '@fiverr-private/layout_components';
import { useGigPageContext } from '@fiverr-private/gig_page_context';
import { Avatar, AvatarImage } from '@fiverr-private/avatar';
import { PriceWithFees, BUYABLE_TYPE } from '@fiverr-private/price_upfront_components';
import { ModalType, openFipMigrationModal } from '@fiverr-private/pro_migration';
import { Text } from '@fiverr-private/typography';
import { I18n } from '@fiverr-private/i18n-react';
import { getContext } from '@fiverr-private/fiverr_context';
import { buildRedirectQueryParameters, BUSINESS_TRIGGER_CTA } from '@fiverr-private/business_success_sign_modal';
import { useEnteredView } from '@fiverr-private/hooks';
import { ContactTrigger } from '../shared/ContactTrigger';
import { HOURLY_CONTACT_BUTTON } from '../../utils/events';
import { reportHourlyCardClick, reportHourlyCardImpression } from '../../utils/biEvents/bianka';
import { isLoggedInUser } from '../../utils/isLoggedInUser';
import { ROLLOUTS } from '../../types/rollouts';
import { INITIAL_TEXT, TRANSLATION_KEY } from './consts';
import { IContactHourlySellerCardProps } from './types';

export const ContactHourlySellerCard: React.FC<IContactHourlySellerCardProps> = ({
  containerStyles,
  className,
  rollouts,
}) => {
  const { sellerCard, seller, currencyConverter, priceDisplayStatus = 'none' } = useGigPageContext();
  const { organizationId } = getContext();

  const hourlyRate = seller?.hourlyRate?.priceInCents;
  const isCurrentUserPro = !!organizationId;
  const observableRef = useRef<HTMLDivElement>(null);

  useEnteredView(observableRef, {
    threshold: 0,
    onEntered: reportHourlyCardImpression,
  });

  const openProMigrationModal = () => {
    openFipMigrationModal({
      variant: ModalType.SeamlessHourly,
      payload: {
        source: 'hourly_gig_request',
        biEnrichment: {
          pageElementName: 'hourly_gig_request',
        },
        onSuccess: () => {
          const url = buildRedirectQueryParameters({
            redirectUrl: getContext().url,
            source: 'hourly_gig_request',
            type: BUSINESS_TRIGGER_CTA.DEFAULT,
          });
          window?.open(url, '_self');
        },
      },
    });
  };

  const isInRollout = rollouts[ROLLOUTS.COBRAS_OPEN_HOURLY_TO_MP];

  const handleClick = (openContact: () => void) => {
    if (isInRollout || isCurrentUserPro || !isLoggedInUser()) return openContact();
    openProMigrationModal();
  };

  return (
    <ContactTrigger
      onClick={reportHourlyCardClick}
      shouldOpenAiBrief
      aiBriefProps={{ initialText: INITIAL_TEXT }}
      source={HOURLY_CONTACT_BUTTON}
    >
      {({ onClick }) => (
        <Stack
          borderColor="grey_400"
          borderRadius="md"
          borderWidth="sm"
          padding="6"
          cursor="pointer"
          direction="column"
          {...containerStyles}
          className={className}
          ref={observableRef}
          onClick={() => handleClick(onClick)}
        >
          <Stack direction="column" gap="1.5">
            <Stack alignItems="center">
              <Avatar size="sm" shape="circle" username={seller?.username || ''} showRing={false}>
                <AvatarImage src={sellerCard?.profilePhoto} alt={seller?.username || ''} />
              </Avatar>
              <Text size="b_sm" fontWeight="bold">
                <I18n k={`${TRANSLATION_KEY}.title`} />
              </Text>
            </Stack>
            <Text size="b_sm" color="bodySecondary">
              <I18n k={`${TRANSLATION_KEY}.description`} />
            </Text>
          </Stack>
          <Stack direction="row" justifyContent="spaceBetween" alignItems="flexEnd">
            {hourlyRate && (
              <Stack direction="column" gap="0">
                <PriceWithFees
                  netPrice={hourlyRate / 100}
                  currency={currencyConverter.getCurrency()}
                  hideTooltip={true}
                  buyableType={BUYABLE_TYPE.HOURLY}
                  typographyProps={{ fontWeight: 'bold', size: 'b_xs' }}
                  descriptionTypographyProps={{ size: 'b_xs' }}
                />
                {priceDisplayStatus !== 'none' && (
                  <Text size="b_xs" color="bodySecondary">
                    <I18n k={`${TRANSLATION_KEY}.taxes_fees`} />
                  </Text>
                )}
              </Stack>
            )}
            <Text size="b_sm" decoration="underline">
              <I18n k={`${TRANSLATION_KEY}.subtitle`} />
            </Text>
          </Stack>
        </Stack>
      )}
    </ContactTrigger>
  );
};
